import type { LibraryAction, WorktreeRetentionMode } from '@nightshift/ui-types';
import type {
  UiWikiPage,
  SharedGraphLayout,
  SharedGraphArrangement,
  UiPromptTemplate,
  TicketMergeReadiness,
  BoardProjection,
  MachineList,
  HarnessCliOverview,
  MintedMachineResponse,
  AssistedRunReport,
  NightManagerEventView,
  NightManagerOverview,
  NightManagerProjectView,
  NightManagerRunView,
  ProjectCompatibilityView,
  ProjectList,
  ProjectListItem,
  TicketList,
  UiActionResult,
  UiConfigResponse,
  UiMilestone,
  UiPhase,
  UiRunsPage,
  UiReviewQueue,
  UiSnapshot,
  UiOps,
  UiReviewQuality,
  UiSpendSeries,
  TicketDetail,
  UiBudgets,
  ReviewerList,
  ReviewerRow,
  PerformanceTargetList,
  PerformanceTargetRow,
  PerformanceKit,
  SettingsUser,
  SettingsUserList,
  AgentToken,
  AgentTokenList,
  MintedAgentTokenResponse,
  UiModelDefaults,
  UiModelEvidence,
  UiRouteApplyResult,
  UiRouteChange,
  UiRouteInventory,
  UiRoutePlan,
  VerificationProfileList,
  VerificationProfileSummary,
  GraphProjection,
  UiCatalogueModel,
  UiCatalogueResponse,
  UiProjectConfiguration,
} from '@nightshift/ui-types';

import type { SidecarSettingsPayload } from '../lib/sidecar-settings.ts';
import { recordApiTiming } from '../lib/client-timing.ts';

/** Re-exported so views import the catalogue shape from the API module they already use. */
export type CatalogueModel = UiCatalogueModel;

export class ApiError extends Error {
  readonly status: number;
  /**
   * The decoded error body, when the server sent one.
   *
   * Some refusals are actionable rather than fatal -- a Start blocked by dependencies ships
   * the blockers so the dialog can offer a way through them -- and a message string alone
   * would force the client to re-parse prose.
   */
  readonly body: Record<string, unknown> | null;

  constructor(message: string, status: number, body: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function readError(
  response: Response,
): Promise<{ message: string; body: Record<string, unknown> | null }> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && !Array.isArray(body)) {
      const record = body as Record<string, unknown>;
      const message = record.message ?? record.error;
      if (typeof message === 'string' && message.length > 0) return { message, body: record };
      return { message: response.statusText || `HTTP ${response.status}`, body: record };
    }
  } catch {
    // fall through to the status text
  }
  return { message: response.statusText || `HTTP ${response.status}`, body: null };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const started = performance.now();
  let status = 0;
  try {
    const response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        accept: 'application/json',
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
    status = response.status;
    if (!response.ok) {
      const failure = await readError(response);
      throw new ApiError(failure.message, response.status, failure.body);
    }
    return (await response.json()) as T;
  } finally {
    // Including the body download and parse: the server's log already has its own half.
    recordApiTiming(path, init?.method ?? 'GET', performance.now() - started, status);
  }
}

export interface UploadedAttachment {
  ok: true;
  id: number;
  filename: string;
  mime: string;
  size: number;
  /** Same-origin path the dashboard renders; also what the runner is told to map. */
  url: string;
  /** `![filename](url)`, ready to splice into the composer. */
  markdown: string;
}

export interface SessionUser {
  id: number;
  username: string;
  displayName: string;
  role: 'admin' | 'member';
}

export interface SessionPresence {
  userId: number;
  displayName: string;
  role: 'admin' | 'member';
  lastSeenAt: number;
}

export interface SessionState {
  authed: boolean;
  authRequired: boolean;
  user?: SessionUser | null;
  presence?: SessionPresence[];
  message?: string;
}

export interface InboxNotification {
  id: number;
  kind: string;
  ref: string | null;
  projectSlug: string | null;
  title: string;
  readAt: number | null;
  createdAt: number;
}

export interface InboxNotificationList {
  unreadCount: number;
  notifications: InboxNotification[];
}

export interface MentionUser {
  id: number;
  username: string;
  displayName: string;
}

type BriefCleanupState = 'queued' | 'processing' | 'succeeded' | 'failed' | 'cancelled' | 'stale';
type BriefCleanupResult =
  | { sidecar: 'brief-cleanup'; outcome: 'unchanged' }
  | { sidecar: 'brief-cleanup'; outcome: 'changed'; problem: string; acceptanceCriteria: string | null };

type TicketDraftState = 'queued' | 'processing' | 'succeeded' | 'failed' | 'cancelled';

/** The generated draft, already constrained to the project's own options by the server. */
interface TicketDraftResult {
  title: string;
  problem: string | null;
  acceptanceCriteria: string | null;
  milestoneId: number | null;
  milestoneName: string | null;
  verificationProfile: string | null;
}

export interface TicketDraftResponse {
  ok: true;
  requestId: number;
  status: TicketDraftState;
  result?: TicketDraftResult;
  /** Choices that disappeared while the request ran, and were cleared rather than applied. */
  warnings?: string[];
  error?: string;
}

export interface BriefCleanupResponse {
  ok: true;
  requestId: number;
  status: BriefCleanupState;
  result?: BriefCleanupResult;
  error?: string;
}

export const api = {
  help: (): Promise<{ pages: UiWikiPage[] }> => request('/api/help'),
  session: () => request<SessionState>('/api/session'),

  login: (username: string, password: string) =>
    request<SessionState>('/api/session', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<SessionState>('/api/session', { method: 'DELETE' }),

  snapshot: (projectSlug: string | null) =>
    request<UiSnapshot>(
      projectSlug ? `/api/snapshot?project=${encodeURIComponent(projectSlug)}` : '/api/snapshot',
    ),

  board: (projectSlug: string | null, milestoneId?: number | null, phaseId?: number | null) => {
    const search = new URLSearchParams();
    if (projectSlug) search.set('project', projectSlug);
    if (milestoneId != null) search.set('milestone', String(milestoneId));
    if (phaseId != null) search.set('phase', String(phaseId));
    const suffix = search.toString();
    return request<BoardProjection>(suffix ? `/api/board?${suffix}` : '/api/board');
  },

  graphLayout: (project: string, mode: string) => request<SharedGraphLayout>(`/api/graph/layout?project=${encodeURIComponent(project)}&mode=${encodeURIComponent(mode)}`),
  saveGraphLayout: (project: string, mode: string, revision: number, arrangement: SharedGraphArrangement) =>
    request<SharedGraphLayout>(`/api/graph/layout?project=${encodeURIComponent(project)}&mode=${encodeURIComponent(mode)}`, {
      method: 'PUT', body: JSON.stringify({ revision, arrangement }),
    }),
  graph: (projectSlug: string) =>
    request<GraphProjection>(`/api/graph?project=${encodeURIComponent(projectSlug)}`),

  tickets: (query: {
    project?: string | null;
    milestone?: number | null;
    phase?: number | null;
    state?: string | null;
    reason?: string | null;
    q?: string | null;
  } = {}) => {
    const search = new URLSearchParams();
    if (query.project) search.set('project', query.project);
    if (query.milestone != null) search.set('milestone', String(query.milestone));
    if (query.phase != null) search.set('phase', String(query.phase));
    if (query.state) search.set('state', query.state);
    if (query.reason) search.set('reason', query.reason);
    if (query.q) search.set('q', query.q);
    const suffix = search.toString();
    return request<TicketList>(suffix ? `/api/tickets?${suffix}` : '/api/tickets');
  },

  milestones: (projectSlug: string | null) => {
    const suffix = projectSlug ? `?project=${encodeURIComponent(projectSlug)}` : '';
    return request<{ ok: true; milestones: UiMilestone[] }>(`/api/milestones${suffix}`);
  },

  createMilestone: (input: { project: string; name: string; description?: string; startAt?: number; endAt?: number }) =>
    request<{ ok: true; inserted: boolean; milestone: UiMilestone }>('/api/milestones', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateMilestone: (id: number, input: { name?: string; description?: string; phase?: string; after?: number[]; orderAfter?: number[] }) =>
    request<{ ok: true; milestone: UiMilestone }>(`/api/milestones/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  phases: (projectSlug: string) =>
    request<{ ok: true; phases: UiPhase[] }>(`/api/phases?project=${encodeURIComponent(projectSlug)}`),

  updatePhase: (id: number, input: { orderAfter?: number[]; name?: string }) =>
    request<{ ok: true; phase: UiPhase }>(`/api/phases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  projects: () => request<ProjectList>('/api/projects'),

  projectCompatibility: (id: number) => request<ProjectCompatibilityView>(`/api/projects/${id}/compatibility`),

  machines: () => request<MachineList>('/api/machines'),

  createMachine: (body: { name: string }) =>
    request<MintedMachineResponse>('/api/machines', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  rotateMachineToken: (id: number) =>
    request<MintedMachineResponse>(`/api/machines/${id}/token`, {
      method: 'POST',
      body: '{}',
    }),

  setMachineLibraries: (id: number, action: LibraryAction) =>
    request<{ ok: true; commandId: string }>(`/api/machines/${id}/libraries`, { method: 'POST', body: JSON.stringify(action) }),

  setMachineStorageMode: (id: number, mode: WorktreeRetentionMode) =>
    request<{ ok: true; mode: WorktreeRetentionMode }>(`/api/machines/${id}/storage`, { method: 'POST', body: JSON.stringify({ mode }) }),

  setMachineWorkers: (id: number, workers: number | null) =>
    request<{ ok: true; name: string; desiredWorkers: number | null }>(
      `/api/machines/${id}/workers`,
      { method: 'POST', body: JSON.stringify({ workers }) },
    ),

  setMachineSidecars: (id: number, sidecars: number | null) =>
    request<{ ok: true; name: string; desiredSidecars: number | null }>(
      `/api/machines/${id}/sidecars`,
      { method: 'POST', body: JSON.stringify({ sidecars }) },
    ),

  setMachineProjects: (id: number, projectIds: number[] | null) =>
    request<{ ok: true; name: string; projectIds: number[] | null; message: string }>(`/api/machines/${id}/projects`, {
      method: 'POST',
      body: JSON.stringify({ projectIds }),
    }),

  setMachinePaused: (id: number, paused: boolean) =>
    request<{ ok: true; name: string; paused: boolean; message: string }>(`/api/machines/${id}/paused`, {
      method: 'POST',
      body: JSON.stringify({ paused }),
    }),

  setMachineCapacityPolicy: (id: number, policy: { floors: Record<string, number>; autoResume: boolean }) =>
    request<{
      ok: true;
      name: string;
      floors: Record<string, number>;
      autoResume: boolean;
      held: boolean;
      message: string;
    }>(`/api/machines/${id}/capacity-policy`, { method: 'POST', body: JSON.stringify(policy) }),

  setMachineCapacityHeld: (id: number, held: boolean) =>
    request<{ ok: true; name: string; held: boolean; message: string }>(`/api/machines/${id}/capacity-hold`, {
      method: 'POST',
      body: JSON.stringify({ held }),
    }),

  releaseDeviceLease: (id: number, attemptId: number) => request<{ ok: boolean; message: string }>(`/api/machines/${id}/device-leases/${attemptId}/release`, { method: 'POST', body: JSON.stringify({ confirmedStopped: true }) }),
  restartMachine: (id: number) =>
    request<{ ok: true; name: string; message: string }>(`/api/machines/${id}/restart`, {
      method: 'POST',
      body: '{}',
    }),

  harnessCli: () => request<HarnessCliOverview>('/api/harness-cli'),

  setHarnessUpdatePolicy: (policy: HarnessCliOverview['policy']) =>
    request<{ ok: true; policy: string; message: string }>('/api/harness-cli/policy', {
      method: 'PUT',
      body: JSON.stringify({ policy }),
    }),

  setMachineHarnessUpdatePolicy: (id: number, policy: HarnessCliOverview['policy'] | null) =>
    request<{ ok: true; policy: string | null; message: string }>(`/api/machines/${id}/harness-cli/policy`, {
      method: 'POST',
      body: JSON.stringify({ policy }),
    }),

  updateHarnessCli: (id: number, harness: string, force = false) =>
    request<{ ok: true; message: string }>(`/api/machines/${id}/harness-cli/${encodeURIComponent(harness)}/update`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),

  checkHarnessLatest: () =>
    request<{ ok: true; overview: HarnessCliOverview }>('/api/harness-cli/latest/check', {
      method: 'POST',
      body: '{}',
    }),

  revokeMachine: (id: number) =>
    request<void>(`/api/machines/${id}`, {
      method: 'DELETE',
      body: '{}',
    }),

  ops: (projectSlug: string | null) => {
    const suffix = projectSlug ? `?project=${encodeURIComponent(projectSlug)}` : '';
    return request<UiOps>(`/api/ops${suffix}`);
  },

  costSeries: (projectSlug: string | null, days: number) => {
    const query = new URLSearchParams({ days: String(days) });
    if (projectSlug) query.set('project', projectSlug);
    return request<UiSpendSeries>(`/api/costs/series?${query.toString()}`);
  },

  reviewQuality: (projectSlug: string | null) => {
    const suffix = projectSlug ? `?project=${encodeURIComponent(projectSlug)}` : '';
    return request<UiReviewQuality>(`/api/review-quality${suffix}`);
  },

  prompts: () => request<{ templates: UiPromptTemplate[] }>('/api/prompts'),
  savePrompt: (id: string, text: string | null, expectedRevision: string) =>
    request<{ template: UiPromptTemplate }>(`/api/prompts/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ text, expectedRevision }) }),
  reviewers: () => request<ReviewerList>('/api/reviewers'),

  createReviewer: (body: {
    name: string;
    prompt: string;
    contextFlags?: ReviewerRow['contextFlags'];
    vendorExclusion?: boolean;
    enabled?: boolean;
  }) =>
    request<ReviewerRow>('/api/reviewers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateReviewer: (
    id: number,
    body: {
      name?: string;
      prompt?: string;
      contextFlags?: ReviewerRow['contextFlags'];
      vendorExclusion?: boolean;
      enabled?: boolean;
    },
  ) =>
    request<ReviewerRow>(`/api/reviewers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  performanceTargets: (project: number | string) =>
    request<PerformanceTargetList>(
      `/api/projects/${encodeURIComponent(String(project))}/performance-targets`,
    ),

  createPerformanceTarget: (
    project: number | string,
    body: { metric: string; comparison: string; value: number; unit?: string | null },
  ) =>
    request<PerformanceTargetRow>(
      `/api/projects/${encodeURIComponent(String(project))}/performance-targets`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updatePerformanceTarget: (
    project: number | string,
    metric: string,
    body: { comparison?: string; value?: number; unit?: string | null },
  ) =>
    request<PerformanceTargetRow>(
      `/api/projects/${encodeURIComponent(String(project))}/performance-targets/${encodeURIComponent(metric)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  deletePerformanceTarget: (project: number | string, metric: string) =>
    request<{ ok: true }>(
      `/api/projects/${encodeURIComponent(String(project))}/performance-targets/${encodeURIComponent(metric)}`,
      { method: 'DELETE', body: JSON.stringify({}) },
    ),

  performanceKit: (project: number | string) =>
    request<PerformanceKit>(
      `/api/projects/${encodeURIComponent(String(project))}/performance-kit`,
    ),

  users: () => request<SettingsUserList>('/api/users'),

  createUser: (body: { username: string; displayName: string; role: 'admin' | 'member'; password: string }) =>
    request<{ ok: true; user: SettingsUser }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  resyncMembers: () =>
    request<{ ok: boolean; synced: number; failed: Array<{ slug: string; error: string }>; membershipSyncedAt: number | null }>(
      '/api/users/members/resync',
      { method: 'POST' },
    ),

  patchUser: (
    id: number,
    body: {
      displayName?: string;
      role?: 'admin' | 'member';
      disabled?: boolean;
      gitlabUserId?: number | null;
    },
  ) =>
    request<{ ok: true; user: SettingsUser }>(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  resetUserPassword: (id: number, password: string) =>
    request<{ ok: true }>(`/api/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  agentTokens: () => request<AgentTokenList>('/api/agent-tokens'),

  mintAgentToken: (body: {
    name: string;
    permission: 'read' | 'write';
    expiresInDays?: number;
    allProjects?: boolean;
    projects?: string[];
  }) =>
    request<MintedAgentTokenResponse & { ok: true }>('/api/agent-tokens', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  patchAgentToken: (id: number, body: { allProjects?: boolean; projects?: string[] }) =>
    request<{ ok: true; token: AgentToken }>(`/api/agent-tokens/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  revokeAgentToken: (id: number) =>
    request<{ ok: true }>(`/api/agent-tokens/${id}/revoke`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  createProject: (body: { slug: string; tracker: string; repo: string; runner?: string }) =>
    request<{
      ok: true;
      action: 'created' | 'updated';
      project: ProjectListItem;
      /** Null when no GitLab hook was written; the reason is in `warnings`. */
      webhook: { action: 'created' | 'updated'; hookId: string; url: string } | null;
      warnings: string[];
    }>('/api/projects', { method: 'POST', body: JSON.stringify(body) }),

  updateProject: (
    id: number | string,
    body: {
      name?: string;
      slug?: string;
      defaultBranch?: string;
      gitRepoPath?: string;
      gitRepoId?: string | null;
    },
  ) =>
    request<{ ok: true; project: ProjectListItem }>(
      `/api/projects/${encodeURIComponent(String(id))}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  saveDeployConfig: (
    id: number | string,
    body: { job?: string; environment?: string; healthUrl?: string },
  ) =>
    request<{
      ok: true;
      project: ProjectListItem;
      deploy: { job: string; environment: string; healthUrl: string } | null;
    }>(`/api/projects/${encodeURIComponent(String(id))}/deploy-config`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  verificationProfiles: () => request<VerificationProfileList>('/api/verification-profiles'),

  updateVerificationProfile: (
    name: string,
    body: {
      ci: 'required' | 'skip';
      buildRequired: boolean;
      unitTestsRequired: boolean;
      humanReviewRequired: boolean;
      reviewerNames: string[];
      captureNames: string[];
      performanceThreshold: { metric: string; comparison: string; value: number } | null;
    },
  ) =>
    request<VerificationProfileSummary>(`/api/verification-profiles/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  budgets: (query: { project?: string | null; ticket?: string; id?: string; from?: number; to?: number; range?: string } = {}) => {
    const search = new URLSearchParams();
    if (query.from !== undefined) search.set('from', String(query.from));
    if (query.to !== undefined) search.set('to', String(query.to));
    if (query.range) search.set('range', query.range);
    if (query.project) search.set('project', query.project);
    if (query.ticket) search.set('ticket', query.ticket);
    if (query.id) search.set('id', query.id);
    const suffix = search.toString();
    return request<UiBudgets>(suffix ? `/api/budgets?${suffix}` : '/api/budgets');
  },

  ticket: (key: string, project?: string) =>
    request<TicketDetail>(
      `/api/tickets/${encodeURIComponent(key)}${project ? `?project=${encodeURIComponent(project)}` : ''}`,
    ),

  /** The whole ref of a document artifact; the inspector payload carries only a preview. */
  artifactText: (id: number) =>
    request<{ id: number; kind: string; text: string }>(`/api/artifacts/${id}/text`),

  runs: (params: {
    project?: string | null;
    stage?: string;
    vendor?: string;
    state?: string;
    status?: string;
    harness?: string;
    outcome?: string;
    ticket?: string;
    limit?: number;
    offset?: number;
  }) => {
    const search = new URLSearchParams();
    if (params.project) search.set('project', params.project);
    if (params.state) search.set('state', params.state);
    if (params.status) search.set('status', params.status);
    if (params.harness) search.set('harness', params.harness);
    if (params.outcome) search.set('outcome', params.outcome);
    if (params.ticket) search.set('ticket', params.ticket);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    return request<UiRunsPage>(`/api/runs?${search.toString()}`);
  },

  config: (projectSlug: string | null) =>
    request<UiConfigResponse>(
      projectSlug ? `/api/config?project=${encodeURIComponent(projectSlug)}` : '/api/config',
    ),

  notifications: () => request<InboxNotificationList>('/api/notifications'),

  mentionUsers: (project: string) =>
    request<{ users: MentionUser[] }>(`/api/projects/${encodeURIComponent(project)}/mention-users`),

  markNotificationRead: (id: number) =>
    request<{ ok: true }>(`/api/notifications/${id}/read`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  markAllNotificationsRead: () =>
    request<{ ok: true }>('/api/notifications/read-all', {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  transcript: (runId: number, opts?: { offset?: number; bytes?: number; lane?: 'attempt' | 'sidecar' }) => {
    const search = new URLSearchParams();
    if (opts?.lane === 'sidecar') search.set('lane', 'sidecar');
    if (opts?.offset !== undefined) search.set('offset', String(Math.max(0, Math.round(opts.offset))));
    if (opts?.bytes !== undefined) search.set('bytes', String(opts.bytes));
    const suffix = search.toString();
    return request<{
      size: number;
      offset: number;
      text: string;
      truncated: boolean;
      missing: boolean;
      /** Set with `missing` when retention removed the file; `retentionDays` when the server knows it. */
      expired?: boolean;
      expiredAt?: number;
      retentionDays?: number;
    }>(`/api/runs/${runId}/transcript${suffix ? `?${suffix}` : ''}`);
  },

  /**
   * Ask a small model to summarise the tail of a run's transcript.
   *
   * Slow by nature (a model process, on the orchestrator's host) and never cached, so the
   * caller owns the spinner and the result. `coversUpTo` is the transcript size the summary
   * was written from; compare it against a later `transcript()` size to know it has aged.
   */
  summariseTranscript: (runId: number) =>
    request<{
      ok: true;
      summary: string;
      model: string;
      bytes: number;
      coversUpTo: number;
      generatedAt: number;
      durationMs: number;
    }>(`/api/runs/${runId}/summary`, { method: 'POST', body: JSON.stringify({}) }),

  /* ── actions ─────────────────────────────────────────────────────────────── */

  action: (path: string, body?: unknown) =>
    request<UiActionResult>(path, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  /**
   * Raw image bytes in, markdown out. Not JSON: the body is the file, the type header says
   * what it is, and the server checks the two agree before storing anything.
   */
  uploadAttachment: async (project: string, file: File): Promise<UploadedAttachment> => {
    const search = new URLSearchParams({ project, filename: file.name || 'image.png' });
    const response = await fetch(`/api/attachments?${search.toString()}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json', 'content-type': file.type },
      body: file,
    });
    if (!response.ok) {
      const failure = await readError(response);
      throw new ApiError(failure.message, response.status, failure.body);
    }
    return (await response.json()) as UploadedAttachment;
  },

  createTicket: (input: {
    project?: string;
    title: string;
    description?: string;
    problem?: string;
    acceptance_criteria?: string;
    ticket_key?: string;
    milestone_id?: number;
    verificationProfile?: string;
  }) => api.action('/api/tickets', input),
  start: (
    key: string,
    body?: {
      expectedVersion?: number;
      activity?: string;
      /** Start past unmet confirmed blockers. Recorded against the ticket. */
      overrideDependencies?: boolean;
      acknowledge?: Array<{ id: number; input_revision: number }>;
    },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/start`, body ?? {}),
  /**
   * Decide one dependency suggestion.
   *
   * `expectedInputRevision` is mandatory on the wire, so a click always answers the evidence
   * the person was actually shown. A 409 comes back carrying the current row.
   */
  decideSuggestion: (
    key: string,
    id: number,
    action: 'accept' | 'flip' | 'dismiss' | 'acknowledge' | 'convert',
    body: {
      expectedInputRevision: number;
      note?: string;
      dependentKey?: string;
      blockerKey?: string;
    },
  ) =>
    api.action(
      `/api/tickets/${encodeURIComponent(key)}/suggestions/${id}/${action}`,
      body,
    ),
  sidecarSettings: (project: string) =>
    request<SidecarSettingsPayload>(`/api/projects/${encodeURIComponent(project)}/sidecars`),
  saveSidecarSettings: (project: string, patch: Record<string, unknown>) =>
    request<Pick<SidecarSettingsPayload, 'settings' | 'budget'>>(
      `/api/projects/${encodeURIComponent(project)}/sidecars`,
      { method: 'PUT', body: JSON.stringify(patch) },
    ),
  rescanProject: (project: string) =>
    request<{ ok: true; tickets: number }>(
      `/api/projects/${encodeURIComponent(project)}/sidecars/rescan`,
      { method: 'POST', body: '{}' },
    ),
  resetSidecars: (project: string) =>
    request<{
      ok: true;
      cancelled: number;
      clearedJobs: number;
      footprints: number;
      suggestions: number;
      decisions: number;
    }>(`/api/projects/${encodeURIComponent(project)}/sidecars/reset`, {
      method: 'POST',
      body: JSON.stringify({ confirm: project }),
    }),
  rescanTicket: (key: string) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/rescan`, {}),
  addDependency: (
    key: string,
    body: { blocker: string; project?: string; note?: string; expectedVersion?: number },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/dependencies`, body),
  /**
   * Start this ticket once `blocker` lands, rather than now.
   *
   * `suggestionId` and `expectedInputRevision` come from the sequencing row that offered it,
   * so the overlap is settled by the same click that schedules the start.
   */
  sequenceAfter: (
    key: string,
    body: {
      blocker: string;
      suggestionId?: number;
      expectedInputRevision?: number;
      note?: string;
      expectedVersion?: number;
    },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/sequence-after`, body),
  removeDependency: (
    key: string,
    blockerKey: string,
    expectedVersion?: number,
    expectedLinkId?: number,
    project?: string,
  ) =>
    request<{ ok: boolean }>(
      `/api/tickets/${encodeURIComponent(key)}/dependencies/${encodeURIComponent(blockerKey)}`,
      {
        method: 'DELETE',
        body: JSON.stringify({
          ...(expectedVersion !== undefined ? { expectedVersion } : {}),
          ...(expectedLinkId !== undefined ? { expectedLinkId } : {}),
          ...(project ? { project } : {}),
        }),
      },
    ),
  recordDone: (key: string, body?: { expectedVersion?: number; note?: string }) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/record-done`, body ?? {}),
  split: (
    key: string,
    children: Array<{ ticket_key: string; title: string; problem?: string }>,
    expectedVersion?: number,
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/split`, { children, expectedVersion }),
  shelve: (key: string, body?: { expectedVersion?: number; reason?: string }) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/shelve`, body ?? {}),
  restore: (key: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/restore`, { expectedVersion }),
  comment: (key: string, body: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/comment`, { body, expectedVersion }),
  /**
   * Record the text as a comment and send it to the attempt running now.
   *
   * Fails, rather than falling back to a plain comment, when nothing is running. The caller
   * asked to reach an agent, and quietly recording a note instead is the exact behaviour that
   * had operators believing they had steered a run.
   */
  steer: (key: string, body: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/steer`, { body, expectedVersion }),
  answer: (key: string, body?: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/answer`, { body, expectedVersion }),
  /** A product decision recorded outside a clarification round. */
  recordDecision: (key: string, body: { question: string; answer: string; expectedVersion?: number }) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/decision`, body),
  /** Accept the readiness preview: move Problem's acceptance section into Acceptance, verbatim. */
  adoptAcceptance: (key: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/adopt-acceptance`, { expectedVersion }),
  planDecision: (
    key: string,
    decision: 'approve' | 'request-changes' | 'defer' | 'reject',
    body?: { expectedVersion?: number; note?: string },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/plan/${decision}`, body ?? {}),
  sendBack: (
    key: string,
    to: 'Refining' | 'Implementing',
    body: { expectedVersion?: number; activity?: string; note: string },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/send-back`, { to, ...body }),
  resetBudget: (key: string, body?: { expectedVersion?: number; family?: string; max?: number; note?: string }) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/reset-budget`, body ?? {}),
  setOverrides: (key: string, overrides: unknown, expectedVersion?: number, expectedOverrides?: unknown) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/overrides`, {
      overrides,
      expectedVersion,
      ...(expectedOverrides !== undefined ? { expectedOverrides } : {}),
    }),
  setCategory: (key: string, category: string | null, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/category`, { category, expectedVersion }),
  updateTicket: (
    key: string,
    body: {
      title?: string;
      problem?: string;
      acceptanceCriteria?: string;
      milestoneId?: number | null;
      expectedVersion?: number;
      expectedFields?: {
        title?: string;
        problem?: string;
        acceptanceCriteria?: string;
        milestoneId?: number | null;
      };
    },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/update`, body),
  createBriefCleanup: (key: string, body: {
    problem: string;
    acceptanceCriteria: string;
    ticketVersion: number;
    storedProblem: string;
    storedAcceptance: string;
  }) => request<BriefCleanupResponse>(`/api/tickets/${encodeURIComponent(key)}/cleanup`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  briefCleanup: (key: string, id: number) =>
    request<BriefCleanupResponse>(`/api/tickets/${encodeURIComponent(key)}/cleanup/${id}`),
  createTicketDraft: (body: { project: string; source: string }) =>
    request<TicketDraftResponse>('/api/ticket-drafts', { method: 'POST', body: JSON.stringify(body) }),
  ticketDraft: (id: number) => request<TicketDraftResponse>(`/api/ticket-drafts/${id}`),
  cancelTicketDraft: (id: number) =>
    request<TicketDraftResponse>(`/api/ticket-drafts/${id}`, { method: 'DELETE' }),
  cancelBriefCleanup: (key: string, id: number) =>
    request<BriefCleanupResponse>(`/api/tickets/${encodeURIComponent(key)}/cleanup/${id}`, { method: 'DELETE' }),
  resume: (key: string, expectedVersion?: number, override = false, note?: string) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/resume`, { expectedVersion, ...(override ? { override: true } : {}), ...(note?.trim() ? { note: note.trim() } : {}) }),
  /** Re-read the ticket's merge request and pipeline from GitLab now; returns a receipt. */
  refreshExternalState: (key: string) => api.action(`/api/tickets/${encodeURIComponent(key)}/refresh`, {}),
  /** The assisted-run report as JSON, for the in-page rendering on the Reports page. */
  assistedRunReport: (url: string) => request<AssistedRunReport>(url),
  /*
   * The night manager.
   *
   * Every mutation carries a request id, because each of them is something a person would not
   * want to happen twice: two runs, two identical messages, a stop applied to a run that had
   * already stopped. The server dedupes on it, so a retry on a flaky connection is safe.
   */
  nightManagerOverview: () => request<NightManagerOverview>('/api/night-manager'),
  nightManagerProject: (slug: string) =>
    request<NightManagerProjectView>(`/api/projects/${encodeURIComponent(slug)}/night-manager`),
  nightManagerRun: (id: string) =>
    request<{ ok: true; run: NightManagerRunView }>(`/api/night-manager/runs/${encodeURIComponent(id)}`),
  saveNightManagerSettings: (
    slug: string,
    body: {
      routes: Array<{ machine: string | null; harness: string; model: string; thinking: string | null }>;
      checkIntervalMinutes: number;
      maxActiveTickets: number;
      instructions: string;
      expectedVersion?: number;
    },
  ) =>
    request<{ ok: true }>(`/api/projects/${encodeURIComponent(slug)}/night-manager/settings`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  enableNightManager: (
    slug: string,
    body: {
      goal: unknown;
      routes: Array<{ machine: string | null; harness: string; model: string; thinking: string | null }>;
      requestId: string;
    },
  ) =>
    request<{ ok: true; run: NightManagerRunView }>(`/api/projects/${encodeURIComponent(slug)}/night-manager/runs`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  editNightManagerRun: (id: string, body: { expectedRevision: number; goal?: unknown; routes?: unknown }) =>
    request<{ ok: true; run: NightManagerRunView }>(`/api/night-manager/runs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  messageNightManager: (id: string, body: { body: string; requestId: string }) =>
    request<{ ok: true; messageId: number; deliveryState: string }>(
      `/api/night-manager/runs/${encodeURIComponent(id)}/messages`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  stopNightManager: (id: string) =>
    request<{ ok: true; alreadyStopped: boolean; message: string; run: NightManagerRunView }>(
      `/api/night-manager/runs/${encodeURIComponent(id)}/stop`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  nightManagerEvents: (id: string, after = 0) =>
    request<{ ok: true; events: NightManagerEventView[]; cursor: number }>(
      `/api/night-manager/runs/${encodeURIComponent(id)}/events?after=${after}`,
    ),
  nightManagerHistory: (slug: string) =>
    request<{ ok: true; total: number; runs: NightManagerRunView[] }>(
      `/api/projects/${encodeURIComponent(slug)}/night-manager/history`,
    ),
  /** Publish the named scenarios' actual images as the new baselines and resume into CI. */
  approveBaselines: (key: string, body: { scenarios: string[]; note?: string; expectedVersion?: number }) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/baseline-update`, body),
  evidenceRepair: (key: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/evidence-repair`, { expectedVersion }),
  /**
   * Rerun the recorded verification job. Every refusal -- the park, the missing job, a retry
   * already in flight, a moved head -- is decided in core and arrives as the error message,
   * so the drawer never has to guess which of them applies.
   */
  retryVerification: (key: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/retry-verification`, { expectedVersion }),
  cancel: (key: string, confirm?: string, expectedVersion?: number, reason?: string) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/cancel`, { confirm, expectedVersion, ...(reason ? { reason } : {}) }),
  wait: (
    key: string,
    body: {
      reason_type: string;
      reason?: string;
      expectedVersion?: number;
      wake_mode?: string;
      /** Required for a dependency wait: the blocker's ticket id, as a string. */
      condition_ref?: string;
    },
  ) => api.action(`/api/tickets/${encodeURIComponent(key)}/wait`, body),
  mergeReadiness: (key: string) =>
    request<TicketMergeReadiness>(`/api/tickets/${encodeURIComponent(key)}/merge-readiness`),
  merge: (key: string, expectedVersion?: number) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/merge`, { expectedVersion }),
  /** Sign off and hand the ticket to the landing loop, which rebases, waits for CI and merges. */
  approve: (key: string, expectedVersion?: number, project?: string) =>
    api.action(`/api/tickets/${encodeURIComponent(key)}/approve`, { expectedVersion, ...(project ? { project } : {}) }),
  /** Every ticket waiting in Human Review across visible projects, with its risk readout. */
  reviewQueue: () => request<UiReviewQueue>('/api/review-queue'),
  deploy: (key: string) => api.action(`/api/tickets/${encodeURIComponent(key)}/deploy`, {}),
  setDispatchPaused: (paused: boolean) => api.action('/api/dispatch', { paused }),
  /**
   * The vendor kill switch. Separate from project configuration on purpose: a project can name
   * any vendor in its routes, and a switch a project could undo would not be a switch.
   */
  setVendorEnabled: (vendor: string, enabled: boolean, reason?: string, project?: string) =>
    api.action('/api/config/vendors', {
      vendor,
      enabled,
      ...(reason ? { reason } : {}),
      ...(project ? { project } : {}),
    }),

  setModelEnabled: (model: string, enabled: boolean, project?: string) =>
    api.action('/api/config/models', { model, enabled, ...(project ? { project } : {}) }),

  modelsCatalogue: (project: string | null) =>
    request<UiCatalogueResponse>(
      project ? `/api/models/catalogue?project=${encodeURIComponent(project)}` : '/api/models/catalogue',
    ),
  refreshModelsCatalogue: () => api.action('/api/models/refresh', {}),
  /** Settings → Nightshift → Models: the default agent routes and the utility routes. */
  modelDefaults: () => request<UiModelDefaults>('/api/models/defaults'),
  saveModelDefaults: (body: Partial<Pick<UiModelDefaults, 'agentRoutes' | 'utilityRoutes' | 'lightRoutes'>>) =>
    request<UiModelDefaults>('/api/models/defaults', { method: 'PUT', body: JSON.stringify(body) }),
  modelEvidence: (days: number) => request<UiModelEvidence>(`/api/models/evidence?days=${encodeURIComponent(String(days))}`),

  /** Settings → Nightshift → Routes. Admin only; a member gets 403. */
  routeInventory: () => request<UiRouteInventory>('/api/routes'),
  planRoutes: (change: UiRouteChange) =>
    request<{ ok: true; plan: UiRoutePlan }>('/api/routes/plan', { method: 'POST', body: JSON.stringify({ change }) }),
  /**
   * Resolves with the server's answer for 200 and 409 alike: a conflict carries the fresh plan
   * the person should look at instead, which is not an error to throw away.
   */
  applyRoutes: async (change: UiRouteChange, token: string): Promise<UiRouteApplyResult> => {
    const response = await fetch('/api/routes/apply', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ change, token }),
    });
    if (response.ok || response.status === 409) return (await response.json()) as UiRouteApplyResult;
    const failure = await readError(response);
    throw new ApiError(failure.message, response.status, failure.body);
  },

  /** The project's one configuration document, with the revision a save must name. */
  projectConfiguration: (project: string) =>
    request<UiProjectConfiguration>(`/api/projects/${encodeURIComponent(project)}/configuration`),
  /**
   * Replace the project's configuration. A stale `expectedRevision` is refused with 409 and an
   * invalid document with 400; both arrive as {@link ApiError} with the server's body attached.
   */
  saveProjectConfiguration: (project: string, configuration: UiProjectConfiguration['configuration'], expectedRevision: number) =>
    request<UiProjectConfiguration>(`/api/projects/${encodeURIComponent(project)}/configuration`, {
      method: 'PUT',
      body: JSON.stringify({ configuration, expectedRevision }),
    }),
};
